import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { Spinner } from '@/src/components/ui/spinner'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { me } from '@/src/api/user'
import { listApiKeys, createApiKey, revokeApiKey, rotateDefaultKey } from '@/src/api/apiKey'
import { listLinkedAccounts, unlinkAccount } from '@/src/api/linkedAccounts'
import { useAuth } from '@/src/components/AuthContextProvider'
import { toast } from 'sonner'
import { ProfileCard } from '@/src/components/manage/account/ProfileCard'
import { PreferencesCard } from '@/src/components/manage/account/PreferencesCard'
import { ApiKeysCard } from '@/src/components/manage/account/ApiKeysCard'
import { LinkedAccountsCard } from '@/src/components/manage/account/LinkedAccountsCard'
import { DangerZoneCard } from '@/src/components/manage/account/DangerZoneCard'
import { CreateApiKeyDialog } from '@/src/components/manage/account/CreateApiKeyDialog'
import { DeleteApiKeyDialog } from '@/src/components/manage/account/DeleteApiKeyDialog'
import { RotateEmailDialog } from '@/src/components/manage/account/RotateEmailDialog'
import { UnlinkAccountDialog } from '@/src/components/manage/account/UnlinkAccountDialog'
import { UpdateProfileDialog } from '@/src/components/manage/account/UpdateProfileDialog'
import { useTranslation } from '@/src/i18n/client'

export const Route = createFileRoute('/_manage/manage/account/')({
  component: AccountPage,
})

function AccountPage() {
  const { t } = useTranslation('manage')
  const { signOut, isAdmin } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false)
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false)
  const [keyToDelete, setKeyToDelete] = useState<{ id: string; key: string } | null>(null)
  const [accountToUnlink, setAccountToUnlink] = useState<{ id: string; providerId: string; accountId: string; createdAt: Date | string } | null>(null)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editDialogInitialFocus, setEditDialogInitialFocus] = useState<'name' | 'email' | undefined>(undefined)
  
  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })
  
  const { data: apiKeys, isLoading: keysLoading } = useQuery({
    queryKey: ['apiKeys', 'list'],
    queryFn: () => listApiKeys(),
  })
  
  const { data: linkedAccounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['linkedAccounts', 'list'],
    queryFn: () => listLinkedAccounts(),
  })
  
  const createKeyMutation = useMutation({
    mutationFn: (name?: string) => createApiKey({ data: { name } }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', 'list'] })
      setNewlyCreatedKey(data.key)
      toast.success(t('account.apiKeyCreated'))
    },
    onError: (error: Error) => {
      toast.error(t('account.failedToCreateApiKey', { message: error.message }))
    },
  })
  
  const revokeKeyMutation = useMutation({
    mutationFn: (id: string) => revokeApiKey({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', 'list'] })
      setDeleteDialogOpen(false)
      setKeyToDelete(null)
      toast.success(t('account.apiKeyRevoked'))
    },
    onError: (error: Error) => {
      toast.error(t('account.failedToRevokeApiKey', { message: error.message }))
    },
  })
  
  const rotateDefaultKeyMutation = useMutation({
    mutationFn: () => rotateDefaultKey(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', 'list'] })
      setRotateDialogOpen(false)
      toast.success(t('account.scanEmailRotated'))
    },
    onError: (error: Error) => {
      toast.error(t('account.failedToRotateKey', { message: error.message }))
    },
  })
  
  const unlinkAccountMutation = useMutation({
    mutationFn: (id: string) => unlinkAccount({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedAccounts', 'list'] })
      setUnlinkDialogOpen(false)
      setAccountToUnlink(null)
      toast.success(t('account.accountUnlinked'))
    },
    onError: (error: Error) => {
      toast.error(t('account.failedToUnlinkAccount', { message: error.message }))
    },
  })
  
  const handleDeleteClick = (key: { id: string; key: string }) => {
    setKeyToDelete(key)
    setDeleteDialogOpen(true)
  }
  
  const handleUnlinkClick = (account: { id: string; providerId: string; accountId: string; createdAt: Date | string }) => {
    setAccountToUnlink(account)
    setUnlinkDialogOpen(true)
  }
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success(t('account.copiedToClipboard'))
  }
  
  // Separate default key from other keys
  const defaultKey = apiKeys?.find(k => k.name === 'Default Key')
  const additionalKeys = apiKeys?.filter(k => k.name !== 'Default Key') || []

  const callbackUrl = '/manage/account'
  const deleteUrl = '/'

  if (isLoading) {
    return (
      <div className="bg-background text-foreground">
        <AdminToolbar>
          <AdminToolbar.Title className="text-lg font-semibold mb-1 md:mb-0">Account Settings</AdminToolbar.Title>
          <AdminToolbar.Right>
            <EditAction
              onClick={() => setEditDialogOpen(true)}
              disabled={!user}
              variant='outline'
              showText
              label={t('manage:account.editProfile')}
            />
          </AdminToolbar.Right>
        </AdminToolbar>
        <div className="flex items-center justify-center min-h-100">
          <Spinner size={48} />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Title className="text-lg font-semibold mb-1 md:mb-0">Account Settings</AdminToolbar.Title>
        <AdminToolbar.Right>
          <EditAction
            onClick={() => setEditDialogOpen(true)}
            disabled={!user}
            variant='outline'
            showText
            label={t('manage:account.editProfile')}
          />
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="space-y-6">
        <ProfileCard 
          user={user || null}
          defaultKey={defaultKey || null}
          isLoadingKey={keysLoading}
          onCopyEmail={copyToClipboard}
          onRotate={() => setRotateDialogOpen(true)}
          onCreateNew={() => rotateDefaultKeyMutation.mutate()}
          isRotating={rotateDefaultKeyMutation.isPending}
          onEditName={() => { setEditDialogInitialFocus('name'); setEditDialogOpen(true) }}
          onEditEmail={() => { setEditDialogInitialFocus('email'); setEditDialogOpen(true) }}
          hasLinkedSocialAccounts={Boolean(linkedAccounts && linkedAccounts.length > 0)}
        />
        
        <PreferencesCard preferences={(user as any)?.preferences} />
        
        <ApiKeysCard
          apiKeys={additionalKeys}
          isLoading={keysLoading}
          onCreate={() => setCreateDialogOpen(true)}
          onDelete={handleDeleteClick}
          onCopyKey={copyToClipboard}
        />
        <LinkedAccountsCard
          accounts={linkedAccounts || []}
          isLoading={accountsLoading}
          onUnlink={handleUnlinkClick}
        />
        
        
        
        <DangerZoneCard
          user={user || null}
          callbackUrl={callbackUrl}
          deleteUrl={deleteUrl}
        />
      </div>
      
      <CreateApiKeyDialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open)
          if (!open) setNewlyCreatedKey(null)
        }}
        onCreate={(name) => createKeyMutation.mutate(name)}
        newlyCreatedKey={newlyCreatedKey}
        isCreating={createKeyMutation.isPending}
      />
      
      <DeleteApiKeyDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        apiKey={keyToDelete}
        onConfirm={() => keyToDelete && revokeKeyMutation.mutate(keyToDelete.id)}
        isDeleting={revokeKeyMutation.isPending}
      />
      
      <RotateEmailDialog
        open={rotateDialogOpen}
        onOpenChange={setRotateDialogOpen}
        currentKey={defaultKey}
        onConfirm={() => rotateDefaultKeyMutation.mutate()}
        isRotating={rotateDefaultKeyMutation.isPending}
      />
      
      <UnlinkAccountDialog
        open={unlinkDialogOpen}
        onOpenChange={setUnlinkDialogOpen}
        account={accountToUnlink}
        onConfirm={() => accountToUnlink && unlinkAccountMutation.mutate(accountToUnlink.id)}
        isUnlinking={unlinkAccountMutation.isPending}
      />

      <UpdateProfileDialog
        open={editDialogOpen}
        onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditDialogInitialFocus(undefined) }}
        user={user || null}
        hasLinkedSocialAccounts={Boolean(linkedAccounts && linkedAccounts.length > 0)}
        initialFocus={editDialogInitialFocus}
      />
    </div>
  )
}
